import {Component, effect, inject, Inject, OnDestroy, OnInit, PLATFORM_ID, signal} from '@angular/core';
import {NgSelectConfig} from '@ng-select/ng-select';
import {filter, Subscription, take} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {EventService} from '@services/event.service';
import {AuthService} from '@services/auth.service';
import {SessionStorageService} from '@services/sessionstorage.service';
import {GuardsCheckStart, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {CurrentService} from "@/app/api/current.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {isPlatformBrowser} from "@angular/common";
import {MessageService} from "@services/message.service";
import {MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarRef} from "@angular/material/snack-bar";
import {MessageComponent} from "@UI/messages/message.component";
import {portalReasonForAuthError} from "@services/portal-redirect";
import {HealthCheckService} from "@services/health-check.service";

const FRONTCHANNEL_LOGOUT_PATH = '/frontchannel-logout';

@Component({
    selector: 'app-root',
    template: `
        @if (ready() || isExternalPage() || isFrontchannelPage()) {
            <router-outlet></router-outlet>
        } @else if (currentFailed()) {
            <div class="loader">
                <div class="load-error">
                    <p>Не удалось загрузить данные пользователя</p>
                    <button type="button" (click)="retryCurrent()">Повторить</button>
                </div>
            </div>
        } @else {
            <div class="loader">
                <mat-progress-spinner diameter="100" mode="indeterminate"></mat-progress-spinner>
            </div>
        }
    `,
    styles: `
        .loader {
            background-color: #6b4bc3;
            display: grid;
            place-items: center;
            height: 100svh;

            mat-progress-spinner {
                --mat-progress-spinner-active-indicator-color: #fff;
            }
        }

        .load-error {
            display: grid;
            gap: 16px;
            justify-items: center;
            color: #fff;
            font-size: 18px;
            text-align: center;

            p {
                margin: 0;
            }

            button {
                padding: 8px 24px;
                border: 1px solid #fff;
                border-radius: 4px;
                background: transparent;
                color: #fff;
                font: inherit;
                cursor: pointer;
            }
        }
    `,
    imports: [
        RouterOutlet,
        MatProgressSpinner,

    ],
    standalone: true
})

export class AppComponent implements OnInit, OnDestroy {

    readonly ready = signal(false);
    /** /current не ответил (сеть, 5xx, 403 без причины): вместо спиннера — сообщение и «Повторить». */
    readonly currentFailed = signal(false);
    readonly isExternalPage = signal(false);
    readonly isFrontchannelPage = signal(window.location.pathname.startsWith(FRONTCHANNEL_LOGOUT_PATH));

    eventBusSub: Subscription = new Subscription();

    title = 'Сервис вступительных и переводных испытаний';

    optionsMessage = inject(MAT_SNACK_BAR_DEFAULT_OPTIONS)

    private readonly healthCheck = inject(HealthCheckService);
    /** Постоянное сообщение о недоступности сервера: живёт до восстановления связи. */
    private healthSnackBar: MatSnackBarRef<MessageComponent> | null = null;

    constructor(private authService: AuthService,
                private eventService: EventService,
                private config: NgSelectConfig,
                private sessionStorage: SessionStorageService,
                private router: Router,
                private titleService: Title,
                private currentService: CurrentService,
                @Inject(PLATFORM_ID) private platformId: Object,
                private toast: MessageService,
    )
    {
        this.config.notFoundText = 'Записей не найдено';

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                // 'returnUrl'/'code='/'state=' — возврат с Keycloak, deep link лежит в returnUrl
                // и не должен быть затёрт. 'frontchannel-logout' — служебная страница, её адрес
                // тоже не должен попасть в returnUrl.
                const exceptions = [
                    'returnUrl',
                    'code=',
                    'state=',
                    'frontchannel-logout'
                ]
                if (!exceptions.some(e => this.router.url.includes(e))) {
                    this.sessionStorage.saveData("returnUrl", this.router.url)
                }
            }
        })

        // Одно сообщение на оба случая — пропала сеть или не отвечает сервер: состояние
        // считает HealthCheckService, поэтому тексты и моменты показа не расходятся.
        effect(() => {
            const unavailable = this.healthCheck.serverUnavailable();
            if (!isPlatformBrowser(this.platformId)) return;

            if (unavailable) {
                if (this.healthSnackBar) return;
                this.healthSnackBar = this.toast.failed('Нет связи с сервером', {duration:0})
                return;
            }

            if (!this.healthSnackBar) return;
            this.healthSnackBar.dismiss();
            this.healthSnackBar = null;
            this.toast.success('Подключение к серверу восстановлено');
        });
    }

    ngOnInit(): void {
        this.router.events.subscribe(event => {
            if (event instanceof GuardsCheckStart) {
                this.titleService.setTitle(this.title);
            }
        });

        // Эта вкладка только что вышла: возвращаемся с end-session, значит SSO-сессия
        // Keycloak уже уничтожена и остальные вкладки можно оповещать — перезагрузятся
        // и уйдут на страницу входа, а не вернутся в приложение.
        this.authService.completeLogout();

        // checkAuth() отработал в APP_INITIALIZER, поэтому состояние уже известно:
        // 1) Есть токен в localStorage — сессия жива: ждём /current, потом открываем интерфейс
        //    и восстанавливаем deep link.
        // 2) Токена нет — открываем сразу: AuthGuard уведёт на Keycloak и вернёт в тот же
        //    раздел (без ready до перехода на Keycloak висел бы спиннер).
        const isLoggedIn = this.authService.isAuthenticated();
        const returnUrl = this.authService.getReturnUrl();
        const isRootPage = window.location.pathname === '/';
        const openApp = () => {
            if (this.ready()) return;
            this.ready.set(true);

            if (isLoggedIn && isRootPage && returnUrl && returnUrl !== this.router.url) {
                this.router.navigateByUrl(returnUrl);
            }
        };

        if (isLoggedIn) {
            // Интерфейс — только после ответа /current. Без доступа к ПИс бэк отвечает
            // 403 NO_ACCESS, без ролей — уходим с no-rights: в обоих случаях уводим в портал,
            // и открытый раньше раздел успевал мелькнуть. Пока ждём — спиннер.
            this.eventBusSub.add(this.currentService.currentError$.subscribe((error) => {
                // 403 с причиной — BasicAuthInterceptor уже уводит в портал; токенов нет —
                // сессия кончилась, идёт переход на Keycloak. Остальное (сеть, 5xx, 403 без
                // причины) — интерфейс не открываем: гарды разделов ждут /current
                // (current.pipe(filter(Boolean))) и без него навигацию не завершат — была бы
                // пустая страница. Показываем ошибку и «Повторить».
                const toPortal = error instanceof HttpErrorResponse && error.status === 403
                    && portalReasonForAuthError(error.error) !== null;
                if (!toPortal && this.authService.isAuthenticated()) this.currentFailed.set(true);
            }));
            this.eventBusSub.add(this.currentService.current
                .pipe(filter(Boolean), take(1))
                .subscribe(() => openApp()));
            this.currentService.getCurrent();
        } else {
            openApp();
        }

        // Сообщение приходит из соседнего документа того же приложения: из другой
        // вкладки — нужен сброс сессии и перезагрузка; из iframe, который открыл
        // Keycloak, — тот же сброс, но уже без нас. Вкладка-инициатор сюда не попадает:
        // её JS-контекст уничтожен навигацией на Keycloak, а документ Keycloak наш
        // канал не видит.
        // Проверка isFrontchannelPage обязательна: сам iframe перезагружать нельзя,
        // иначе он наслал бы сообщение по кругу.
        this.eventBusSub.add(this.authService.remoteLogout$.subscribe(() => {
            this.authService.logoffLocal();
            if (!this.isFrontchannelPage()) {
                window.location.reload();
            }
        }));

        this.eventBusSub.add(this.eventService.on('logout', () => {
            this.logout();
        }));
        this.eventBusSub.add(this.eventService.on('endSession', () => {
            this.endSession();
        }));

        this.eventBusSub.add(this.eventService.on('exit', () => {
            this.exit();
        }));

        if (isPlatformBrowser(this.platformId)) {
            this.healthCheck.start();
        }
    }

    ngOnDestroy(): void {
        if (this.eventBusSub)
            this.eventBusSub.unsubscribe();
        this.healthCheck.stop();
    }

    /** Повтор /current после ошибки: снова спиннер; ответ откроет интерфейс или уведёт в портал. */
    retryCurrent(): void {
        this.currentFailed.set(false);
        this.currentService.getCurrent();
    }

    /**
     * Сессия Keycloak закончилась: уходим на SSO с текущим адресом, чтобы после
     * повторного входа пользователь оказался в том же разделе.
     */
    endSession(): void {
        const url = this.router.url;
        if (!url || url.includes('logout')) return;

        // Токены гасим до ухода на Keycloak: иначе authorize успеет молча перевыпустить
        // их на месте и вернуть пользователя в приложение. На сам логаут это не влияет —
        // редирект уводит в другое хранилище, где наши токены не лежат.
        this.authService.logoffLocal();
        this.authService.loginSso(url || '/');
    }

    logout(): void {
        this.authService.logout()
    }

    exit(): void {
        // Для OIDC переход на /login делает сам провайдер в postLogoutRedirectUri,
        // локальная навигация создала бы двойной переход.
        this.authService.logout()
    }


}
